// hh-l4a — единый маппинг типа собственности (Property.type) на иконку и
// русскую подпись. Бэкенд хранит `type` как строку из набора
// apartment / house / dacha / garage / car / other. Используется в дереве
// имущества, пикерах места, экранах переноса и форме выбора типа (aidepo-46) —
// везде, где тип выводится подписью/иконкой или выбирается пользователем.

import type { IconName } from './icons';

/** Известные значения Property.type. */
export type PropertyType = 'apartment' | 'house' | 'dacha' | 'garage' | 'car' | 'other';

interface PropertyTypeMeta {
  readonly icon: IconName;
  readonly label: string;
}

// Иконки берутся из реестра icons.tsx: apartment / dacha / garageBuilding /
// car существуют как отдельные глифы; для дома используем `home`, для «другого»
// — нейтральный `box` (отличный от фолбэка `home`, чтобы выбор «Другое»
// читался как осознанный, а не как «тип не распознан»).
const PROPERTY_TYPE_META: Record<PropertyType, PropertyTypeMeta> = {
  apartment: { icon: 'apartment', label: 'Квартира' },
  house: { icon: 'home', label: 'Дом' },
  dacha: { icon: 'dacha', label: 'Дача' },
  garage: { icon: 'garageBuilding', label: 'Гараж' },
  car: { icon: 'car', label: 'Автомобиль' },
  other: { icon: 'box', label: 'Другое' },
};

/**
 * Порядок типов для отрисовки чипов в форме выбора (aidepo-46). Совпадает с
 * порядком из acceptance-критериев задачи: apartment, house, dacha, garage,
 * car, other.
 */
export const PROPERTY_TYPE_OPTIONS: readonly PropertyType[] = [
  'apartment',
  'house',
  'dacha',
  'garage',
  'car',
  'other',
];

// Фолбэк для неизвестного / пустого type — нейтральная папка-дом.
const FALLBACK_META: PropertyTypeMeta = { icon: 'home', label: 'Объект' };

/** True, если строка — одно из известных значений PropertyType. */
function isKnownType(type: string): type is PropertyType {
  return Object.prototype.hasOwnProperty.call(PROPERTY_TYPE_META, type);
}

/** Иконка + подпись для типа — для отрисовки чипа выбора (aidepo-46). */
export function propertyTypeMeta(type: PropertyType): { readonly icon: IconName; readonly label: string } {
  return PROPERTY_TYPE_META[type];
}

// Правила инференса типа по названию (aidepo-46). Словарь ключевых слов
// синхронизирован с PROPERTY_RULES в iconResolve.ts (там слова маппятся на
// иконки, здесь — на сам тип). `other` сюда не входит: это явный пользова-
// тельский выбор, а не результат догадки. Порядок важен — первое совпадение
// выигрывает; более специфичные слова идут раньше общих.
const TYPE_INFERENCE_RULES: ReadonlyArray<readonly [readonly string[], PropertyType]> = [
  [['квартир', 'апартамент', 'студи'], 'apartment'],
  [['дач'], 'dacha'],
  [['гараж', 'гск', 'бокс', 'погреб', 'подвал', 'кладов', 'сара'], 'garage'],
  [['машин', 'автомобил', 'авто', 'тачк', 'мотоцикл'], 'car'],
  [['дом', 'коттедж', 'таунхаус', 'изб', 'особняк'], 'house'],
];

/** Нормализация названия: lowercase + «ё»→«е» — как в iconResolve.normalize. */
function normalizeName(name: string): string {
  return name.toLowerCase().replace(/ё/g, 'е').trim();
}

/**
 * Пытается определить тип собственности по её названию (aidepo-46).
 * Возвращает первый тип, чьё ключевое слово встречается в названии, или
 * `null`, если ни одно правило не сработало. Вызывающая сторона решает, что
 * делать с `null` — НЕ дефолтит молча в apartment.
 *
 * Примеры: «Гараж в ГСК» → garage, «Дача в Подмосковье» → dacha,
 * «Toyota Camry» → null (латиница без ключевых слов), «Подвал» → garage.
 */
export function inferPropertyType(name: string | null | undefined): PropertyType | null {
  const text = normalizeName(name ?? '');
  if (text.length === 0) return null;
  for (const [keywords, type] of TYPE_INFERENCE_RULES) {
    for (const kw of keywords) {
      if (text.includes(kw)) return type;
    }
  }
  return null;
}

/** Иконка для типа собственности. Неизвестный тип → нейтральный `home`. */
export function propertyTypeIcon(type: string | null | undefined): IconName {
  if (type != null && isKnownType(type)) return PROPERTY_TYPE_META[type].icon;
  return FALLBACK_META.icon;
}

/**
 * Русская подпись типа собственности. Неизвестный непустой тип возвращается
 * как есть (чтобы не терять данные), пустой/отсутствующий → «Объект».
 */
export function propertyTypeLabel(type: string | null | undefined): string {
  if (type != null && isKnownType(type)) return PROPERTY_TYPE_META[type].label;
  if (type != null && type.trim().length > 0) return type;
  return FALLBACK_META.label;
}
